import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, ChevronDown } from 'lucide-react';
import {
  ModelSelection,
  OTHER_MODEL,
  OTHER_REPAIR,
  PART_QUALITIES,
  PHONE_MODELS,
  PartQuality,
  REPAIR_TYPES,
  RepairSelection,
} from './types';
import { getPrice } from './priceTable';

interface PhoneRepairEstimatorProps {
  whatsappUrl: string;
}

const PhoneRepairEstimator: React.FC<PhoneRepairEstimatorProps> = ({ whatsappUrl }) => {
  const [model, setModel] = useState<ModelSelection | ''>('');
  const [repair, setRepair] = useState<RepairSelection | ''>('');
  const [quality, setQuality] = useState<PartQuality | ''>('');
  const [otherModelText, setOtherModelText] = useState('');
  const [otherRepairText, setOtherRepairText] = useState('');

  const isOtherModel = model === OTHER_MODEL;
  const isOtherRepair = repair === OTHER_REPAIR;

  const modelLabel = isOtherModel ? (otherModelText.trim() || 'modèle Android non précisé') : model;
  const repairLabel = isOtherRepair
    ? (otherRepairText.trim() || 'panne non listée')
    : REPAIR_TYPES.find((r) => r.id === repair)?.label ?? '';
  const qualityLabel = PART_QUALITIES.find((q) => q.id === quality)?.label ?? '';

  const price = useMemo(() => {
    if (!model || !repair || !quality || isOtherRepair) return undefined;
    return getPrice(repair, quality);
  }, [model, repair, quality, isOtherRepair]);

  const quoteMessage = useMemo(() => {
    if (!model || !repair || !quality) return '';
    if (price === undefined) {
      return `Bonjour, je souhaite un devis pour un remplacement ${repairLabel} (pièce ${qualityLabel}) sur ${modelLabel} (panne non listée, merci de me faire un devis personnalisé).`;
    }
    return `Bonjour, je souhaite un devis pour un remplacement ${repairLabel} (pièce ${qualityLabel}) sur ${modelLabel}, prix indicatif ${price}€.`;
  }, [model, repair, quality, price, repairLabel, qualityLabel, modelLabel]);

  const showResult = Boolean(
    model &&
      repair &&
      quality &&
      (!isOtherModel || otherModelText.trim()) &&
      (!isOtherRepair || otherRepairText.trim())
  );

  return (
    <div className="bg-gradient-to-b from-white/10 to-white/[0.03] rounded-2xl p-6 md:p-8 border border-white/15 max-w-2xl mx-auto">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div>
          <label htmlFor="phone-model" className="block text-sm text-gray-300 mb-2">
            Modèle
          </label>
          <div className="relative">
            <select
              id="phone-model"
              value={model}
              onChange={(e) => setModel(e.target.value as ModelSelection)}
              className="w-full appearance-none rounded-lg bg-[#2F3136] border border-white/15 text-white pl-4 pr-10 py-2.5 text-sm focus:outline-none focus:border-[#5865F2]"
            >
              <option value="">Choisir un modèle</option>
              {PHONE_MODELS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
              <option value={OTHER_MODEL}>Android</option>
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          </div>
          {isOtherModel && (
            <input
              type="text"
              value={otherModelText}
              onChange={(e) => setOtherModelText(e.target.value)}
              placeholder="Précisez votre modèle Android"
              className="mt-2 w-full rounded-lg bg-[#2F3136] border border-white/15 text-white px-4 py-2.5 text-sm placeholder:text-gray-500 focus:outline-none focus:border-[#5865F2]"
            />
          )}
        </div>

        <div>
          <label htmlFor="repair-type" className="block text-sm text-gray-300 mb-2">
            Type de panne
          </label>
          <div className="relative">
            <select
              id="repair-type"
              value={repair}
              onChange={(e) => setRepair(e.target.value as RepairSelection)}
              className="w-full appearance-none rounded-lg bg-[#2F3136] border border-white/15 text-white pl-4 pr-10 py-2.5 text-sm focus:outline-none focus:border-[#5865F2]"
            >
              <option value="">Choisir une panne</option>
              {REPAIR_TYPES.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label}
                </option>
              ))}
              <option value={OTHER_REPAIR}>Autre panne</option>
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          </div>
          {isOtherRepair && (
            <input
              type="text"
              value={otherRepairText}
              onChange={(e) => setOtherRepairText(e.target.value)}
              placeholder="Précisez la panne"
              className="mt-2 w-full rounded-lg bg-[#2F3136] border border-white/15 text-white px-4 py-2.5 text-sm placeholder:text-gray-500 focus:outline-none focus:border-[#5865F2]"
            />
          )}
        </div>

        <div>
          <label htmlFor="part-quality" className="block text-sm text-gray-300 mb-2">
            Qualité de la pièce
          </label>
          <div className="relative">
            <select
              id="part-quality"
              value={quality}
              onChange={(e) => setQuality(e.target.value as PartQuality)}
              className="w-full appearance-none rounded-lg bg-[#2F3136] border border-white/15 text-white pl-4 pr-10 py-2.5 text-sm focus:outline-none focus:border-[#5865F2]"
            >
              <option value="">Choisir une qualité</option>
              {PART_QUALITIES.map((q) => (
                <option key={q.id} value={q.id}>
                  {q.label}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          </div>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {showResult && (
          <motion.div
            key={`${model}-${repair}-${quality}`}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="rounded-xl border border-[#5865F2]/35 bg-[#5865F2]/10 p-5"
          >
            {price !== undefined ? (
              <>
                <p className="text-[#AEB9FF] text-xs uppercase tracking-wide mb-1">Prix estimé</p>
                <p className="text-white text-2xl font-bold mb-1">{price}€</p>
                <p className="text-gray-300 text-xs mb-4">Prix indicatif, confirmé après diagnostic.</p>
              </>
            ) : (
              <p className="text-gray-200 text-sm mb-4">
                Cette combinaison n'est pas encore dans notre grille de prix. Contactez-nous pour un devis personnalisé.
              </p>
            )}
            <a
              href={`${whatsappUrl}?text=${encodeURIComponent(quoteMessage)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-5 py-2.5 bg-gradient-to-r from-[#5865F2] via-[#4752C4] to-[#5865F2] text-white text-sm font-medium rounded-lg hover:shadow-lg hover:shadow-[#5865F2]/30 transition-all duration-300"
            >
              Demander ce devis sur WhatsApp <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PhoneRepairEstimator;
